import { EyeIcon } from "@heroicons/react/outline";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { TableUtil } from "@tyrannosaurustech/ui";

import { getRequestsFromRequests } from "../../common/request";
import { Badge } from "../../components/atoms/Badge";
import { Button } from "../../components/atoms/Button";
import { Theme } from "../../components/atoms/theme";
import { Tooltip } from "../../components/atoms/Tooltip";
import { ParagraphS, TitleM } from "../../components/atoms/Typography";
import { ContentWrapper } from "../../components/ContentWrapper";
import { MiniStatusBadge } from "../../components/molecules/MiniStatusBadge";
import { CoreTable } from "../../components/organisms/CoreTable";
import { useAppSelector } from "../../hooks/redux";
import { useGetFirmQuery } from "../../redux/reduxQuery/firmApi";
import { useGetRequests } from "../../redux/reduxQuery/requestApi";
import { filterDuplicateSelectOptions } from "../../utils/filterDuplicateSelectOptions";
import { filtersRequest } from "../../utils/filtersRequest";
import { statusColor } from "../../utils/statusColor";
import { statusLabel } from "../../utils/statusLabel";
import { formatDate } from "../../utils/formatDate";
import useToast from "../../hooks/useToast";

const RequestList = ({ requests }) => (
  <UL>
    {requests.map((request, index) => (
      <li key={`${request}-${index}`}>{request}</li>
    ))}
  </UL>
);

const UL = styled.ul`
  margin: 0;
  padding-left: 0.938rem;
`;
const Ellipsis = styled.p`
  width: 10rem;
  margin: 0;
  display: -webkit-box;
  overflow: hidden;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

interface selectProps {
  value: string;
  label: string;
}

const providerStatus = [
  {
    value: "verified",
    label: "Verified",
  },
  {
    value: "unverified",
    label: "Unverified",
  },
  {
    value: "outdated",
    label: "Outdated",
  },
];

const hasMeaningfulValue = (value: unknown): boolean => {
  if (value === null || value === undefined) {
    return false;
  }
  if (typeof value === "string") {
    return value.trim().length > 0;
  }
  if (Array.isArray(value)) {
    return value.some(hasMeaningfulValue);
  }
  if (typeof value === "object") {
    return Object.values(value as Record<string, unknown>).some(
      hasMeaningfulValue
    );
  }
  return true;
};

const countActiveFilters = (filters: Record<string, unknown> = {}) =>
  Object.values(filters).reduce<number>(
    (count, value) => (hasMeaningfulValue(value) ? count + 1 : count),
    0
  );

const combineQuerySegments = (base: string, dynamic: string) => {
  const sanitizedBase = base || "";
  const sanitizedDynamic = dynamic || "";

  if (!sanitizedBase && !sanitizedDynamic) {
    return "";
  }
  if (!sanitizedBase) {
    return sanitizedDynamic.startsWith("&")
      ? sanitizedDynamic.slice(1)
      : sanitizedDynamic;
  }
  if (!sanitizedDynamic) {
    return sanitizedBase;
  }
  if (sanitizedDynamic.startsWith("&")) {
    return `${sanitizedBase}${sanitizedDynamic}`;
  }
  return `${sanitizedBase}&${sanitizedDynamic}`;
};

const sanitizeProviderStatusFilter = (filters: Record<string, unknown>) => {
  return Object.entries(filters).reduce<Record<string, unknown>>(
    (acc, [key, value]) => {
      if (
        key === "providerStatus" &&
        value &&
        typeof value === "object" &&
        !Array.isArray(value) &&
        (value as Record<string, unknown>).hasOwnProperty("name")
      ) {
        acc[key] = (value as Record<string, unknown>).name as unknown;
      } else {
        acc[key] = value;
      }
      return acc;
    },
    {}
  );
};

const pruneEmptyFilters = (filters: Record<string, unknown>) => {
  return Object.entries(filters).reduce<Record<string, unknown>>(
    (acc, [key, value]) => {
      if (hasMeaningfulValue(value)) {
        acc[key] = value;
      }
      return acc;
    },
    {}
  );
};

const reshapeFiltersForQuery = (filters: Record<string, unknown> = {}) => {
  return Object.entries(filters).reduce<Record<string, unknown>>(
    (acc, [key, value]) => {
      if (key === "orderBy") {
        acc[key] = value;
        return acc;
      }

      if (key === "providerStatus") {
        acc["verif"] = value;
        return acc;
      }

      const isObjectValue =
        value !== null && typeof value === "object" && !Array.isArray(value);
      const valueHasName =
        isObjectValue &&
        (value as Record<string, unknown>).hasOwnProperty("name");

      if (isObjectValue && !valueHasName) {
        if (key.includes(".")) {
          const rootKey = key.split(".")[0];
          if (!acc[rootKey]) {
            acc[rootKey] = { name: value };
          }
        } else if (key.endsWith("Name")) {
          const rootKey = key.slice(0, -"Name".length);
          if (!acc[rootKey]) {
            acc[rootKey] = { name: value };
          }
        } else {
          acc[key] = value;
        }
      } else {
        acc[key] = value;
      }

      return acc;
    },
    {}
  );
};

export default function ClientPortalHome() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { data: firm } = useGetFirmQuery(id);
  const globalClientFilters = useAppSelector(
    (state) => state.filter?.clientrequest
  );
  const sanitizedGlobalFilters = useMemo(
    () =>
      pruneEmptyFilters(
        sanitizeProviderStatusFilter(globalClientFilters ?? {})
      ),
    [globalClientFilters]
  );

  const [patients, setPatients] = useState<selectProps[]>([]);
  const [medicalProvider, setMedicalProvider] = useState<selectProps[]>([]);
  const [openFilters, setOpenFilters] = useState(false);
  const [requestError, setRequestError] = useState<string | null>(null);

  const toast = useToast();
  const lastToastMessageRef = useRef<string | null>(null);

  const baseFilter = useMemo(() => (id ? `firmId=${id}` : ""), [id]);

  const formatQueryString = useCallback(
    (filters: Record<string, unknown>) => {
      const normalizedFilters = reshapeFiltersForQuery(filters);
      const dynamicFilters = filtersRequest(normalizedFilters, false, 0);
      return combineQuerySegments(baseFilter, dynamicFilters);
    },
    [baseFilter]
  );

  const [filterValues, setFilterValues] = useState<Record<string, unknown>>(
    sanitizedGlobalFilters
  );
  const [dataToFetch, setDataToFetch] = useState<string>(() =>
    formatQueryString(sanitizedGlobalFilters)
  );

  const getData = useGetRequests(dataToFetch);
  const [config, refetch] = TableUtil.useAsyncConfig(getData, 50);
  const {
    asyncConfig,
    data: tableData = [],
    loading: isLoading,
    error: tableError,
  } = config;

  const handleFilterValuesChange = useCallback(
    (incomingFilters: Record<string, unknown>) => {
      const sanitizedFilters = sanitizeProviderStatusFilter(
        incomingFilters ?? {}
      );
      const cleanedFilters = pruneEmptyFilters(sanitizedFilters);
      setFilterValues((prev) => {
        const prevEntries = Object.entries(prev);
        const nextEntries = Object.entries(cleanedFilters);
        if (prevEntries.length !== nextEntries.length) {
          return cleanedFilters;
        }
        const hasDifference = prevEntries.some(([key, prevValue]) => {
          const nextValue = cleanedFilters[key];
          if (hasMeaningfulValue(prevValue) !== hasMeaningfulValue(nextValue)) {
            return true;
          }
          if (hasMeaningfulValue(prevValue)) {
            return JSON.stringify(prevValue) !== JSON.stringify(nextValue);
          }
          return false;
        });
        return hasDifference ? cleanedFilters : prev;
      });
    },
    []
  );

  useEffect(() => {
    handleFilterValuesChange(sanitizedGlobalFilters);
  }, [sanitizedGlobalFilters, handleFilterValuesChange]);

  useEffect(() => {
    const nextQuery = formatQueryString(filterValues);
    setDataToFetch((prev) => (prev === nextQuery ? prev : nextQuery));
  }, [filterValues, formatQueryString]);

  const isFirstFetch = useRef(true);
  useEffect(() => {
    // Skip first render - useAsyncConfig already fetches on mount
    if (isFirstFetch.current) {
      isFirstFetch.current = false;
      return;
    }
    refetch();
  }, [dataToFetch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isLoading || tableError) {
      return;
    }

    const patientOptions = tableData.map((item: any) => ({
      value: item.patient?.id,
      label: item.patient?.name,
    }));
    setPatients((prevState) =>
      filterDuplicateSelectOptions([...prevState, ...patientOptions])
    );
    const providerOptions = tableData.map((item: any) => ({
      value: item.provider?.id,
      label: item.provider?.name,
    }));
    setMedicalProvider((prevState) =>
      filterDuplicateSelectOptions([...prevState, ...providerOptions])
    );
  }, [isLoading, tableError, tableData]);

  useEffect(() => {
    if (tableError) {
      const message = tableError.trim()
        ? tableError
        : "Something went wrong while fetching requests.";
      setRequestError((prev) => (prev === message ? prev : message));
      setPatients((prev) => (prev.length ? [] : prev));
      setMedicalProvider((prev) => (prev.length ? [] : prev));
      if (lastToastMessageRef.current !== message) {
        toast.error(message);
        lastToastMessageRef.current = message;
      }
    } else if (requestError) {
      setRequestError(null);
      lastToastMessageRef.current = null;
    }
  }, [tableError, toast, requestError]);

  const activeFilterCount = useMemo(
    () => countActiveFilters(filterValues),
    [filterValues]
  );

  const filterButton = useMemo(
    () => ({
      label: `Filters${activeFilterCount ? ` (${activeFilterCount})` : ""}`,
      icon: "AdjustmentsVerticalIcon",
      base: "secondary",
      onClick: () => setOpenFilters(true),
      btnContainerClasses:
        "box-border border-2 border-primary-400 border-solid",
    }),
    [activeFilterCount]
  );

  const actionButtonsConfig = useMemo(() => {
    const buttons: Array<{
      label: string;
      icon: string;
      base: string;
      onClick: () => void;
      btnContainerClasses: string;
    }> = [];

    const canCreateRequests =
      firm?.canCreateRequests !== undefined ? firm.canCreateRequests : true;

    if (canCreateRequests) {
      buttons.push({
        label: "New Request",
        icon: "PlusIcon",
        base: "primary",
        onClick: () => {
          if (id) {
            navigate(`/client/${id}/new-request`);
          }
        },
        btnContainerClasses: "box-border",
      });
    }

    return buttons;
  }, [id, navigate, firm]);

  const actionRightButtons = useMemo(() => [filterButton], [filterButton]);

  const columns = useMemo(
    () => [
      {
        id: "patientName",
        Header: "Patient",
        header: "Patient",
        accessorKey: "patient.name",
        accessor: "patient.name",
        filterName: "patientName",
        options: patients,
        includeToolTip: true,
        size: 200,
      },
      {
        id: "status",
        Header: "Status",
        header: "Status",
        accessorKey: "status",
        accessor: "status",
        filterName: "status",
        cell: (cell) => (
          <ParagraphS
            color={statusColor[cell.row.original.status]}
            style={{ margin: 0, textTransform: "capitalize" }}
          >
            {statusLabel[cell.row.original.status]}
          </ParagraphS>
        ),
      },
      {
        id: "providerName",
        Header: "Medical Provider",
        header: "Medical Provider",
        accessorKey: "provider.name",
        accessor: "provider.name",
        filterName: "providerName",
        options: medicalProvider,
        includeToolTip: true,
        size: 200,
      },
      {
        id: "requests",
        Header: "Requests",
        header: "Requests",
        accessorFn: (row: any) => getRequestsFromRequests(row).length,
        size: 120,
        cell: (cellProps) => {
          const request = cellProps.row.original;
          const requests = getRequestsFromRequests(request);
          return (
            <Tooltip
              text={<RequestList requests={requests} />}
              position="bottom"
              background={Theme.colors.neutrals_000}
            >
              <Badge
                icon={true}
                color={Theme.colors.primaries_000}
                hoverColor={Theme.colors.primaries_neg100}
                badgeText={`${requests.length}`}
                img={
                  <EyeIcon
                    width={"0.938rem"}
                    color={Theme.colors.neutrals_500}
                  />
                }
              />
            </Tooltip>
          );
        },
      },
      {
        Header: "Verif.",
        id: "providerStatus",
        filterName: "providerStatus",
        header: "Verif.",
        accessorFn: (row: any) => row?.provider?.status,
        accessor: (row: any) => row?.provider?.status,
        options: providerStatus,
        size: 100,
        cell: (cell) => (
          <MiniStatusBadge
            id={`Verified-${cell.row.original.id}`}
            status={cell.row.original.provider?.status}
          />
        ),
      },
      {
        id: "serviceDates",
        Header: "Service Dates",
        header: "Service Dates",
        accessorKey: "serviceDates",
        accessor: "serviceDates",
        filterName: "serviceDates",
        cell: (cell) => {
          const row = cell.row.original;
          if (row.serviceDateFrom && row.serviceDateTo) {
            return `${formatDate(row.serviceDateFrom)} - ${formatDate(
              row.serviceDateTo
            )}`;
          }
          return row.serviceDates || "";
        },
      },
      {
        id: "requestNotes",
        Header: "Notes",
        header: "Notes",
        accessorKey: "requestNotes",
        accessor: "requestNotes",
        filterName: "requestNotes",
        includeToolTip: true,
        cell: (cell) => <Ellipsis>{cell.getValue() as string}</Ellipsis>,
      },
      {
        id: "submittedDate",
        Header: "Submitted Date",
        header: "Submitted Date",
        accessorKey: "submittedDate",
        accessor: "submittedDate",
        disableFilters: true,
        cell: (cell) => {
          const value = cell.getValue() as string | undefined;
          const submittedDate = value ? new Date(value) : null;
          return (
            <ParagraphS>
              {submittedDate ? submittedDate.toLocaleDateString() : ""}
            </ParagraphS>
          );
        },
      },
      {
        id: "actions",
        Header: "Actions",
        header: "",
        accessorKey: "id",
        disableFilters: true,
        enableSorting: false,
        size: 120,
        cell: (cell) => (
          <div className="flex justify-end">
            <Button
              type="button"
              icon={true}
              color={Theme.colors.primaries_000}
              hoverColor={Theme.colors.primaries_neg100}
              noText={false}
              style={{ marginLeft: "auto" }}
              onClick={() =>
                navigate(`/client/${id}/requests/${cell.row.original.id}`)
              }
              img={
                <EyeIcon width={"0.938rem"} color={Theme.colors.neutrals_500} />
              }
            />
          </div>
        ),
      },
    ],
    [id, medicalProvider, navigate, patients]
  );

  return (
    <ContentWrapper>
      {firm && (
        <Fragment>
          <TitleM style={{ padding: "24px 0px" }}>{firm.name}</TitleM>
          {requestError && (
            <ParagraphS
              color={Theme.colors.danger_000}
              style={{ marginBottom: "1rem" }}
            >
              {requestError}
            </ParagraphS>
          )}
          {firm.canCreateRequests === false && (
            <ParagraphS
              color={Theme.colors.warning_000}
              style={{
                marginBottom: "1rem",
                padding: "0.75rem",
                backgroundColor: Theme.colors.warning_200,
                borderRadius: "0.5rem",
              }}
            >
              Request creation has been disabled for this law firm. You can
              still view existing requests.
            </ParagraphS>
          )}
          <CoreTable
            resizableColumns={true}
            tableType="clientrequest"
            columns={columns}
            data={tableData}
            loading={isLoading}
            actionButtonsConfig={actionButtonsConfig}
            actionRightButtons={actionRightButtons}
            openFilters={openFilters}
            setOpenFilters={setOpenFilters}
            navigation={() => {}}
            setFilterValues={handleFilterValuesChange}
            asyncConfig={asyncConfig}
          />
        </Fragment>
      )}
    </ContentWrapper>
  );
}
